import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';

function useScrollY() {
  const [scrollY, setScrollY] = useState(() => window.scrollY);
  useEffect(() => {
    const h = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);
  return scrollY;
}

function useInView(threshold = 0.01) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) { setInView(true); return; }
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function LiftCard({ children, className, style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={`group ${className ?? ''}`}
      style={{ ...style, transform: hovered ? 'translateY(-6px) scale(1.015)' : 'translateY(0) scale(1)', transition: hovered ? 'transform 0.18s cubic-bezier(.22,1,.36,1)' : 'transform 0.40s cubic-bezier(.22,1,.36,1)', willChange: 'transform', position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="absolute inset-0 pointer-events-none" style={{ borderRadius: 'inherit', background: 'rgba(196,168,130,0.08)', opacity: hovered ? 1 : 0, transition: 'opacity 0.18s ease' }} />
      {children}
    </div>
  );
}

interface BentoService {
  key: string;
  badge: { en: string; fr: string };
  title: { en: string; fr: string };
  price: { en: string; fr: string };
  priceSubNote?: { en: string; fr: string };
  priceNote: { en: string; fr: string };
  cta: { en: string; fr: string };
  icon: string;
}

const bentoServices: BentoService[] = [
  {
    key: 'discovery',
    badge: { en: 'Not sure what you want?', fr: 'Vous ne savez pas ce que vous voulez?' },
    title: { en: 'Matchmaking Lab: Every new vehicle model suitable for you', fr: 'Matchmaking: Tous les modèles de véhicules neufs disponibles' },
    price: { en: 'Our Gift to You', fr: 'Offert Sans Frais' },
    priceNote: {
      en: 'Move forward with clarity and confidence. Answer a few simple questions and our platform delivers a tailored shortlist of all new vehicles that fit your needs, budget, and preferences — with all the details and expert recommendations.',
      fr: 'Avancez avec clarté et confiance. Répondez à quelques questions et notre plateforme vous propose une liste sur mesure de tous les modèles de véhicules neufs adaptés à vos besoins, votre budget et vos préférences — avec tous les détails et spécifications de chacun.'
    },
    cta: { en: 'Start Matchmaking Analysis', fr: 'Débuter le Matchmaking' },
    icon: 'ri-search-eye-line',
  },
  {
    key: 'endtoend',
    badge: { en: 'Already know what you want?', fr: 'Vous savez ce que vous voulez?' },
    title: { en: 'Sourcing, Negotiation & Management', fr: 'Acquisition, Negotiation et Gestion' },
    price: { en: '$299', fr: '299$' },
    priceSubNote: {
      en: 'Includes a 60 MIN virtual consultation. 1/2 paid upfront, the other 1/2 after delivery.',
      fr: 'Comprend une consultation virtuelle de 60 MIN. 50 % payable à l\'avance, 50 % à la livraison.',
    },
    priceNote: {
      en: "Once you know what model you want, we locate the right vehicle, negotiate the best deal, discuss and educate you on financing VS leasing VS outright purchasing and manage the entire buying process from A to Z — saving you time, money, and effort.",
      fr: "Une fois que vous savez quel modèle vous voulez, nous localisons le bon véhicule, négocions les meilleurs conditions, comparons et expliquons le financement VS la location VS l'achat et gérons tout le processus A à Z — vous faisant économiser temps, argent et efforts."
    },
    cta: { en: 'Start Sourcing Process', fr: "Débuter l'Acquisition" },
    icon: 'ri-route-line',
  },
];

const inputCls = "w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 text-sm bg-[#f5f0e8] border border-[#d4c4a8] focus:ring-[#c4a882] text-[#2a2219] placeholder:text-[#9a8870]";
const selectCls = "w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 text-sm bg-[#f5f0e8] border border-[#d4c4a8] focus:ring-[#c4a882] text-[#2a2219]";
const textareaCls = "w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 text-sm bg-[#f5f0e8] border border-[#d4c4a8] focus:ring-[#c4a882] text-[#2a2219] resize-none placeholder:text-[#9a8870]";
const submitBtnCls = "w-full text-[#0e0b07] py-4 rounded-lg font-bold transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base tracking-wide";

function SuccessState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 gap-4">
      <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(196,168,130,0.15)', border: '1px solid rgba(196,168,130,0.4)' }}>
        <i className="ri-check-line text-3xl text-[#c4a882]"></i>
      </div>
      <p className="text-lg font-semibold text-[#2a2219] text-center">{message}</p>
    </div>
  );
}

export default function HeroSection() {
  const { lang, setLang } = useLanguage();
  const scrollY = useScrollY();
  const { ref: heroRef, inView } = useInView(0.01);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const urlLang = searchParams.get('lang');
    if (urlLang === 'en' || urlLang === 'fr') {
      setLang(urlLang);
      setSearchParams({}, { replace: true });
    }
  }, []);

  const [revealIndex, setRevealIndex] = useState(0);
  const advance = useCallback(() => setRevealIndex(prev => prev + 1), []);

  // Independent toggle: both cards can be open simultaneously
  const [openCards, setOpenCards] = useState<Set<string>>(new Set());
  const toggleCard = (key: string) => setOpenCards(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  useEffect(() => {
    if (inView && revealIndex === 0) { const t = setTimeout(() => setRevealIndex(1), 80); return () => clearTimeout(t); }
  }, [inView, revealIndex]);

  useEffect(() => {
    if (revealIndex === 1) { const t = setTimeout(() => setRevealIndex(2), 420); return () => clearTimeout(t); }
    if (revealIndex === 2) { const t = setTimeout(() => setRevealIndex(3), 350); return () => clearTimeout(t); }
    if (revealIndex >= 3 && revealIndex <= 4) { const t = setTimeout(advance, 320); return () => clearTimeout(t); }
  }, [revealIndex, advance]);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false); };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const [showModal, setShowModal] = useState(false);
  const [modalSubmitted, setModalSubmitted] = useState(false);

  const contentShift = scrollY * 0.18;
  const fadeContent = Math.max(0, 1 - scrollY * 0.0008);
  const p1 = scrollY * 0.12;
  const p2 = scrollY * 0.20;
  const p3 = scrollY * 0.28;

  const eyebrowText = lang === 'fr' ? "Matchmaking INDÉPENDANT de véhicules et ACQUISITION" : "INDEPENDENT Vehicle matchmaking & sourcing";
  const headlineText = lang === 'fr' ? "La Voie Intelligente Vers Votre Prochain Achat" : "The Intelligent Path to Your Next Purchase";
  const subheadingText = lang === 'fr' ? "Service-Conseil pour une sélection et une acquisition de véhicules éclairées" : "Consulting for smarter vehicle selection and sourcing";

  const tx = {
    en: {
      navCta: 'Get In Touch',
      getInTouchTitle: 'Get In Touch',
      getInTouchSubtitle: "Fill out the form below and we'll get back to you within 24 hours.",
      getInTouchEmail: 'You can also reach us at',
      getInTouchSuccess: 'Thanks! We look forward to connecting with you.',
      name: 'Full Name *', namePh: 'John Smith',
      email: 'Email Address *', emailPh: 'john@example.com',
      phone: 'Phone Number *', phonePh: '+1 (555) 000-0000',
      langLabel: 'Preferred Language *', langEn: 'English', langFr: 'French',
      message: 'How can we help you? *', messagePh: 'Describe how we can help',
      maxChars: 'Maximum 500 characters',
      submit: 'Submit Request',
      consent: 'I consent to having this website store my submitted information so they can respond to my inquiry.',
    },
    fr: {
      navCta: 'Nous Joindre',
      getInTouchTitle: 'Nous Joindre',
      getInTouchSubtitle: "Remplissez le formulaire ci-dessous et nous vous contacterons dans les 24 heures.",
      getInTouchEmail: 'Vous pouvez aussi nous écrire à',
      getInTouchSuccess: 'Merci! Nous avons hâte de vous parler.',
      name: 'Nom Complet *', namePh: 'Jean Dupont',
      email: 'Adresse Courriel *', emailPh: 'jean@exemple.com',
      phone: 'Numéro de Téléphone *', phonePh: '+1 (555) 000-0000',
      langLabel: 'Langue préférée *', langEn: 'Anglais', langFr: 'Français',
      message: 'Comment pouvons-nous vous aider? *', messagePh: 'Décrivez comment nous pouvons vous aider',
      maxChars: 'Maximum 500 caractères',
      submit: 'Soumettre la Demande',
      consent: "J'accepte que ce site web conserve les informations soumises afin de répondre à ma demande.",
    },
  }[lang];

  const scrollToServices = () => {
    const el = document.getElementById('pricing');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <style>{`
        @keyframes cursor-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
        @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes topo-drift { 0% { transform: translate(0,0) scale(1.04); } 50% { transform: translate(-8px, -12px) scale(1.06); } 100% { transform: translate(0,0) scale(1.04); } }
        @keyframes grid-pulse { 0%, 100% { opacity: 0.06; } 50% { opacity: 0.12; } }
        @keyframes chevron-bounce { 0%, 100% { transform: translateY(0px); opacity: 0.45; } 50% { transform: translateY(7px); opacity: 0.85; } }
        @media (max-width: 640px) { #hero-bg-img { object-position: center center; } }
      `}</style>

      {/* ── Navigation ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 h-14 sm:h-20 transition-all duration-500" style={{ background: 'transparent', backdropFilter: 'none', borderBottom: 'none' }}>
        <div className="h-full px-4 sm:px-8 lg:px-20 flex items-center justify-end gap-2">

          <div
            className="flex items-center gap-0.5 rounded-full p-0.5"
            style={{
              opacity: inView ? Math.max(0, 1 - scrollY / 80) : 0,
              transform: inView ? 'translateY(0)' : 'translateY(-18px)',
              transition: 'transform 1.1s cubic-bezier(.22,1,.36,1) 0.25s',
              pointerEvents: scrollY > 80 ? 'none' : 'auto',
              background: 'rgba(196,168,130,0.08)',
              border: '1px solid rgba(196,168,130,0.30)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <button
              onClick={() => setLang('en')}
              className="px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer"
              style={lang === 'en' ? { background: '#c4a882', color: '#0e0b07' } : { color: 'rgba(196,168,130,0.65)' }}
            >EN</button>
            <button
              onClick={() => setLang('fr')}
              className="px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer"
              style={lang === 'fr' ? { background: '#c4a882', color: '#0e0b07' } : { color: 'rgba(196,168,130,0.65)' }}
            >FR</button>
          </div>

          <div ref={menuRef} className="relative" style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(-18px)', transition: 'opacity 1.1s cubic-bezier(.22,1,.36,1) 0.35s, transform 1.1s cubic-bezier(.22,1,.36,1) 0.35s' }}>
            <button onClick={() => setMenuOpen(prev => !prev)} className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200 whitespace-nowrap" style={{ background: menuOpen ? 'rgba(196,168,130,0.18)' : 'rgba(196,168,130,0.08)', border: '1px solid rgba(196,168,130,0.35)', backdropFilter: 'blur(8px)' }}>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-[#e8d8c0] text-base`}></i>
              </div>
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-12 flex flex-col gap-3 p-4 rounded-2xl min-w-[220px]" style={{ background: 'rgba(14,11,7,0.92)', backdropFilter: 'blur(20px)', border: '1px solid rgba(196,168,130,0.18)', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', animation: 'slide-up 0.2s cubic-bezier(.22,1,.36,1) both' }}>
                <div style={{ borderTop: '1px solid rgba(196,168,130,0.12)' }} className="flex flex-col gap-1 pt-1">
                  <Link to="/matchmaking-platform" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap hover:bg-[rgba(196,168,130,0.08)]" style={{ color: 'rgba(232,216,192,0.8)' }}>
                    <i className="ri-search-eye-line text-[#c4a882] text-base"></i>
                    {lang === 'fr' ? 'Plateforme de Matchmaking' : 'Matchmaking Lab'}
                  </Link>
                  <Link to="/end-to-end-service" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap hover:bg-[rgba(196,168,130,0.08)]" style={{ color: 'rgba(232,216,192,0.8)' }}>
                    <i className="ri-route-line text-[#c4a882] text-base"></i>
                    {lang === 'fr' ? 'Acquisition Professionnelle' : 'Sourcing & Negotiation'}
                  </Link>
                </div>
                <button onClick={() => { setShowModal(true); setMenuOpen(false); }} className="w-full px-4 py-2.5 rounded-lg transition-all whitespace-nowrap cursor-pointer text-sm font-bold text-[#0e0b07] tracking-wide" style={{ background: 'linear-gradient(135deg, #c4a882 0%, #a8906a 100%)' }}>
                  {tx.navCta}
                </button>
              </div>
            )}
          </div>

        </div>
      </nav>

      {/* ── Hero ── */}
      <section ref={heroRef} className="relative overflow-hidden min-h-[100dvh] flex flex-col" style={{ background: '#0e0b07' }}>
        {/* BG image */}
        <div className="absolute inset-0 pointer-events-none">
          <img id="hero-bg-img" src="https://public.readdy.ai/ai/img_res/de670cd856356a790b933be61e125bfa.jpg" alt="" aria-hidden="true" className="w-full h-full object-cover" style={{ objectPosition: '50% 40%', opacity: 0.78 }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom right, rgba(8,10,8,0.62) 0%, rgba(10,12,10,0.28) 45%, rgba(8,10,8,0.55) 100%)' }} />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 60% 50% at 30% 40%, rgba(196,168,130,0.05) 0%, transparent 60%)' }} />
          <div className="absolute inset-0" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Cpath d='M0 200 Q100 150 200 200 Q300 250 400 200' stroke='%23c4a882' stroke-width='0.6' fill='none' opacity='0.18'/%3E%3Cpath d='M0 180 Q100 120 200 170 Q300 220 400 170' stroke='%23c4a882' stroke-width='0.5' fill='none' opacity='0.12'/%3E%3Cpath d='M0 220 Q100 180 200 230 Q300 280 400 230' stroke='%23c4a882' stroke-width='0.5' fill='none' opacity='0.12'/%3E%3Cpath d='M0 160 Q80 100 200 140 Q320 180 400 140' stroke='%23c4a882' stroke-width='0.4' fill='none' opacity='0.08'/%3E%3Cpath d='M0 240 Q80 210 200 260 Q320 310 400 260' stroke='%23c4a882' stroke-width='0.4' fill='none' opacity='0.08'/%3E%3C/svg%3E")`, backgroundSize: '400px 400px', animation: 'topo-drift 20s ease-in-out infinite', opacity: 1 }} />
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(196,168,130,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(196,168,130,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px', animation: 'grid-pulse 8s ease-in-out infinite' }} />
          <div className="absolute top-0 left-0 right-0" style={{ height: '220px', background: 'linear-gradient(to bottom, rgba(8,10,8,0.72) 0%, transparent 100%)' }} />
          <div className="absolute bottom-0 left-0 right-0" style={{ height: '320px', background: 'linear-gradient(to top, #08080f 0%, rgba(8,8,15,0.7) 50%, transparent 100%)' }} />
        </div>

        {/* Parallax layers */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse 80% 60% at 70% -10%, rgba(196,168,130,0.04) 0%, transparent 60%)`, transform: `translateY(${p1}px)` }} />
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(196,168,130,0.4) 1px, transparent 1px)', backgroundSize: '28px 28px', opacity: 0.04, transform: `translateY(${p2}px)` }} />
        <div className="absolute pointer-events-none" style={{ top: '-120px', right: '-160px', width: '820px', height: '820px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(196,168,130,0.05) 0%, transparent 68%)', transform: `translateY(${p3}px)` }} />

        <div className="relative z-10 flex flex-col items-center flex-1 justify-center pt-24 sm:pt-28 pb-14 sm:pb-20 px-5 sm:px-10 lg:px-20 text-center" style={{ transform: `translateY(${-contentShift}px)` }}>
          <div className="w-full max-w-4xl flex flex-col items-center">

            <div style={{ opacity: fadeContent }} className="w-full flex flex-col items-center">
              <div className="flex items-center justify-center gap-2 sm:gap-3 mb-4 sm:mb-9" style={{ opacity: revealIndex >= 1 ? 1 : 0, transform: revealIndex >= 1 ? 'translateY(0)' : 'translateY(-10px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}>
                <div className="h-px w-3 sm:w-12" style={{ background: 'linear-gradient(to right, transparent, #c4a882)' }} />
                <span className="text-[7px] sm:text-sm font-bold tracking-[0.04em] sm:tracking-[0.22em] uppercase whitespace-nowrap" style={{ color: '#c4a882', fontFamily: 'Satoshi, Inter, sans-serif' }}>{eyebrowText}</span>
                <div className="h-px w-3 sm:w-12" style={{ background: 'linear-gradient(to left, transparent, #c4a882)' }} />
              </div>

              <h1 className="text-2xl sm:text-6xl lg:text-7xl font-black text-white leading-[1.05] tracking-tight mb-4 sm:mb-9" style={{ fontFamily: 'Satoshi, Inter, sans-serif', opacity: revealIndex >= 1 ? 1 : 0, transform: revealIndex >= 1 ? 'translateY(0)' : 'translateY(-12px)', transition: 'opacity 0.55s cubic-bezier(.22,1,.36,1), transform 0.55s cubic-bezier(.22,1,.36,1)', textShadow: '0 2px 40px rgba(0,0,0,0.8)' }}>
                {headlineText}
              </h1>


            </div>

            <div className="w-full flex flex-col items-center">
              <div
                className="mb-3 sm:mb-6 flex items-center justify-center gap-3 w-full max-w-2xl mx-auto"
                style={{ opacity: revealIndex >= 3 ? 1 : 0, transform: revealIndex >= 3 ? 'translateY(0)' : 'translateY(12px)', transition: 'opacity 0.5s ease, transform 0.5s ease', pointerEvents: 'none' }}
              >
                <span className="text-[10px] sm:text-xs font-bold tracking-widest uppercase px-2" style={{ color: 'rgba(196,168,130,0.6)' }}>{lang === 'fr' ? 'Nos Services' : 'Our Services'}</span>
              </div>

              {/* Service cards — both can be open simultaneously */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 w-full max-w-2xl mx-auto">
                {bentoServices.map((svc, i) => {
                  const visible = revealIndex >= 3 + i;
                  const isExpanded = openCards.has(svc.key);
                  return (
                    <div
                      key={svc.key}
                      className="flex flex-col"
                      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(36px)', transition: 'opacity 0.5s cubic-bezier(.22,1,.36,1), transform 0.5s cubic-bezier(.22,1,.36,1)', pointerEvents: visible ? 'auto' : 'none' }}
                    >
                      <LiftCard className="relative flex flex-col rounded-xl sm:rounded-2xl p-3.5 sm:p-7 overflow-hidden cursor-default h-full" style={{ background: 'rgba(14,11,7,0.28)', backdropFilter: 'blur(3px)', border: '1px solid rgba(196,168,130,0.18)', boxShadow: 'inset 0 1px 0 rgba(196,168,130,0.10), 0 4px 32px rgba(0,0,0,0.15)' }}>
                        <div className="absolute top-0 left-0 right-0 h-px pointer-events-none" style={{ background: 'linear-gradient(90deg, transparent, rgba(196,168,130,0.5), transparent)' }} />

                        <div className="flex items-start gap-2 mb-3 sm:mb-5">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(196,168,130,0.10)', border: '1px solid rgba(196,168,130,0.25)' }}>
                            <i className={`${svc.icon} text-sm sm:text-lg text-[#c4a882]`}></i>
                          </div>
                          <div className="flex-1 min-w-0 text-left">
                            <span className="block text-[10px] sm:text-xs font-bold tracking-wider" style={{ color: 'rgba(196,168,130,0.7)' }}>{svc.badge[lang]}</span>
                            <h3 className="text-xs sm:text-base font-black text-white leading-tight mt-0.5" style={{ fontFamily: 'Satoshi, Inter, sans-serif' }}>
                              {svc.key === 'discovery' && svc.title[lang].includes(': ') ? (
                                <>
                                  {svc.title[lang].split(': ')[0]}
                                  <span className="font-medium block sm:inline" style={{ color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>
                                    {' '}{svc.title[lang].split(': ').slice(1).join(' ')}
                                  </span>
                                </>
                              ) : svc.title[lang]}
                            </h3>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 text-left mb-2 sm:mb-3">
                          <div className="flex flex-col items-start gap-0.5">
                            {svc.key === 'endtoend' ? (
                              <>
                                <div className="flex items-baseline gap-1">
                                  <span className="text-base sm:text-2xl font-black text-white">{lang === 'fr' ? '149$' : '$149'}</span>
                                  <span className="text-[9px] sm:text-[11px] font-semibold" style={{ color: 'rgba(196,168,130,0.65)' }}>{lang === 'fr' ? "à l'avance, inclut 60 min consultation virtuelle." : 'upfront, includes 60 mins virtual consultation.'}</span>
                                </div>
                                <div className="flex items-baseline gap-1">
                                  <span className="text-base sm:text-2xl font-black text-white">{lang === 'fr' ? '149$' : '$149'}</span>
                                  <span className="text-[9px] sm:text-[11px] font-semibold" style={{ color: 'rgba(196,168,130,0.65)' }}>{lang === 'fr' ? 'à la livraison' : 'after delivery'}</span>
                                </div>
                              </>
                            ) : (
                              <>
                                <span className="text-base sm:text-2xl font-black text-white flex-shrink-0">{svc.price[lang]}</span>
                                {svc.key === 'discovery' && (
                                  <span className="text-[9px] sm:text-[11px] font-semibold tracking-wide uppercase" style={{ color: '#c4a882' }}>
                                    {lang === 'fr' ? 'pour une durée limitée seulement' : 'for a limited time only'}
                                  </span>
                                )}
                                {svc.priceSubNote && (
                                  <span className="text-[9px] sm:text-[11px] font-bold tracking-wide" style={{ color: 'rgba(196,168,130,0.65)' }}>
                                    {svc.priceSubNote[lang]}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          <button onClick={() => toggleCard(svc.key)} className="ml-auto flex items-center gap-1 text-xs font-bold cursor-pointer whitespace-nowrap transition-all duration-200" style={{ color: '#c4a882' }}>
                            <span style={{ display: 'inline-block', transform: isExpanded ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.25s ease', lineHeight: 1, fontSize: '15px', fontWeight: 700 }}>+</span>
                            <span>{isExpanded ? (lang === 'fr' ? 'Moins' : 'Less') : (lang === 'fr' ? 'Détails' : 'Details')}</span>
                          </button>
                        </div>

                        <div style={{ maxHeight: isExpanded ? '200px' : '0px', opacity: isExpanded ? 1 : 0, overflow: 'hidden', transition: 'max-height 0.35s cubic-bezier(.22,1,.36,1), opacity 0.25s ease', marginBottom: isExpanded ? '14px' : '0px' }}>
                          <p className="text-xs sm:text-sm leading-relaxed text-left" style={{ color: 'rgba(232,216,192,0.8)' }}>{svc.priceNote[lang]}</p>
                        </div>

                        <button
                          onClick={() => {
                            if (svc.key === 'discovery') navigate('/matchmaking-platform');
                            else navigate('/end-to-end-service');
                          }}
                          className="mt-auto w-full py-2 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold tracking-wide transition-all duration-200 cursor-pointer whitespace-nowrap"
                          style={{ background: 'rgba(196,168,130,0.12)', border: '1px solid rgba(196,168,130,0.30)', color: '#e8d8c0' }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(196,168,130,0.22)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(196,168,130,0.50)'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(196,168,130,0.12)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(196,168,130,0.30)'; }}
                        >
                          {svc.cta[lang]}<i className="ri-arrow-right-line ml-1.5 text-xs"></i>
                        </button>
                      </LiftCard>
                    </div>
                  );
                })}
              </div>


            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          className="absolute bottom-7 left-0 right-0 flex justify-center pointer-events-none z-20"
          style={{
            opacity: fadeContent * (revealIndex >= 4 ? 1 : 0),
            transition: 'opacity 0.8s ease',
          }}
        >
          <div className="flex flex-col items-center gap-0.5" style={{ animation: 'chevron-bounce 2.2s ease-in-out infinite' }}>
            <i className="ri-arrow-down-s-line" style={{ color: 'rgba(196,168,130,0.5)', fontSize: '22px', lineHeight: 1 }}></i>
            <i className="ri-arrow-down-s-line" style={{ color: 'rgba(196,168,130,0.22)', fontSize: '16px', lineHeight: 1, marginTop: '-8px' }}></i>
          </div>
        </div>
      </section>

      {/* ── Get In Touch Modal ── */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(14,11,7,0.85)', backdropFilter: 'blur(12px)' }} onClick={() => { setShowModal(false); setModalSubmitted(false); }}>
          <div className="rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" style={{ background: '#faf6f0', border: '1px solid rgba(196,168,130,0.25)' }} onClick={(e) => e.stopPropagation()}>
            <div className="p-4 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-[#1a1510]">{tx.getInTouchTitle}</h2>
                <button onClick={() => { setShowModal(false); setModalSubmitted(false); }} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#e8d8c0] transition-colors cursor-pointer">
                  <i className="ri-close-line text-2xl text-[#4a3f30]"></i>
                </button>
              </div>
              {modalSubmitted ? (
                <SuccessState message={tx.getInTouchSuccess} />
              ) : (
                <>
                  <p className="text-[#6a5a45] mb-4 text-sm">{tx.getInTouchSubtitle}</p>
                  <div className="flex items-center gap-2 mb-6 px-4 py-3 rounded-xl" style={{ background: 'rgba(196,168,130,0.08)', border: '1px solid rgba(196,168,130,0.22)' }}>
                    <i className="ri-mail-line text-[#c4a882] text-base"></i>
                    <span className="text-sm text-[#4a3f30]">{tx.getInTouchEmail} <a href="mailto:info@automatchlab.ca" className="font-semibold text-[#c4a882] hover:underline">info@automatchlab.ca</a></span>
                  </div>
                  <form id="get-in-touch-form" data-readdy-form action="https://readdy.ai/api/form/d8d9e73a-9ea6-4eaa-8d9b-3b5e0fbbcbf9" method="POST"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const formData = new FormData(form);
                      const data = new URLSearchParams();
                      formData.forEach((value, key) => { data.append(key, value.toString()); });
                      fetch('https://readdy.ai/api/form/d8d9e73a-9ea6-4eaa-8d9b-3b5e0fbbcbf9', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: data.toString() })
                        .then(res => { if (res.ok) { setModalSubmitted(true); form.reset(); } });
                    }}
                    className="space-y-5"
                  >
                    <div><label className="block text-sm font-medium text-[#1a1510] mb-2">{tx.name}</label><input type="text" name="name" required className={inputCls} placeholder={tx.namePh} /></div>
                    <div><label className="block text-sm font-medium text-[#1a1510] mb-2">{tx.email}</label><input type="email" name="email" required className={inputCls} placeholder={tx.emailPh} /></div>
                    <div><label className="block text-sm font-medium text-[#1a1510] mb-2">{tx.phone}</label><input type="tel" name="phone" required className={inputCls} placeholder={tx.phonePh} /></div>
                    <div>
                      <label className="block text-sm font-medium text-[#1a1510] mb-2">{tx.langLabel}</label>
                      <select name="preferred_language" required className={selectCls}>
                        <option value="">{tx.langLabel.replace(' *', '')}</option>
                        <option value="English">{tx.langEn}</option>
                        <option value="French">{tx.langFr}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1a1510] mb-2">{tx.message}</label>
                      <textarea name="message" required maxLength={500} rows={4} className={textareaCls} placeholder={tx.messagePh}></textarea>
                      <div className="text-xs text-[#9a8870] mt-1">{tx.maxChars}</div>
                    </div>
                    <div className="flex items-start gap-3">
                      <input type="checkbox" name="consent" id="git-consent" required className="mt-1 w-4 h-4 flex-shrink-0 cursor-pointer accent-[#c4a882]" />
                      <label htmlFor="git-consent" className="text-xs text-[#6a5a45] leading-relaxed cursor-pointer">{tx.consent}</label>
                    </div>
                    <button type="submit" className={submitBtnCls} style={{ background: 'linear-gradient(135deg, #c4a882 0%, #a8906a 100%)' }}>{tx.submit}</button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
